import * as React from 'react';
import { Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useUserSearch, type UserSearchResult } from '@/hooks/useUserSearch';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const TOKEN_SPLIT_REGEX = /[,;\s]+/;

export function splitEmailTokens(input: string): string[] {
  return input
    .split(TOKEN_SPLIT_REGEX)
    .map((token) => token.trim())
    .filter(Boolean);
}

export function isValidTebraEmail(email: string): boolean {
  return EMAIL_REGEX.test(email) && email.toLowerCase().endsWith('@tebra.com');
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function formatUserResult(result: UserSearchResult): string {
  return `${result.name || result.email} <${result.email}>`;
}

function suggestionSearchText(email: string): string {
  const localPart = email.split('@')[0] || email;
  const firstSegment = localPart.split(/[._+-]/)[0] || localPart;
  return firstSegment.slice(0, 20);
}

function compactComparable(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function levenshteinDistance(left: string, right: string): number {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);

  for (let leftIndex = 0; leftIndex < left.length; leftIndex += 1) {
    let lastDiagonal = previous[0];
    previous[0] = leftIndex + 1;

    for (let rightIndex = 0; rightIndex < right.length; rightIndex += 1) {
      const insertion = previous[rightIndex + 1] + 1;
      const deletion = previous[rightIndex] + 1;
      const substitution = lastDiagonal + (left[leftIndex] === right[rightIndex] ? 0 : 1);
      lastDiagonal = previous[rightIndex + 1];
      previous[rightIndex + 1] = Math.min(insertion, deletion, substitution);
    }
  }

  return previous[right.length];
}

function findCloseSuggestion(
  email: string,
  results: UserSearchResult[]
): UserSearchResult | undefined {
  const normalizedEmail = normalizeEmail(email);
  const normalizedLocalPart = compactComparable(normalizedEmail.split('@')[0] || normalizedEmail);

  return results
    .map((result) => {
      const resultEmail = normalizeEmail(result.email);
      const resultLocalPart = compactComparable(resultEmail.split('@')[0] || resultEmail);
      const emailDistance = levenshteinDistance(normalizedEmail, resultEmail);
      const localDistance = levenshteinDistance(normalizedLocalPart, resultLocalPart);

      return {
        result,
        score: Math.min(emailDistance, localDistance),
        isClose:
          resultEmail !== normalizedEmail &&
          ((normalizedLocalPart.length >= 5 && localDistance <= 2) ||
            (normalizedEmail.length >= 10 && emailDistance <= 3)),
      };
    })
    .filter((candidate) => candidate.isClose)
    .sort((left, right) => left.score - right.score)[0]?.result;
}

export interface TokenizedEmailAutocompleteProps {
  id: string;
  value: string[];
  onChange: (emails: string[]) => void;
  draftValue?: string;
  onDraftChange?: (draft: string) => void;
  onInvalidTokensChange?: (invalidTokens: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  maxEmails?: number;
  autoFocus?: boolean;
  ariaLabel?: string;
  commitOnBlur?: boolean;
}

export function TokenizedEmailAutocomplete({
  id,
  value,
  onChange,
  draftValue,
  onDraftChange,
  onInvalidTokensChange,
  placeholder = 'email1@tebra.com, email2@tebra.com',
  disabled,
  maxEmails,
  autoFocus,
  ariaLabel,
  commitOnBlur,
}: TokenizedEmailAutocompleteProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [uncontrolledInputValue, setUncontrolledInputValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);
  const [invalidTokens, setInvalidTokens] = React.useState<string[]>([]);
  const [knownEmails, setKnownEmails] = React.useState<Set<string>>(new Set());
  const [suggestionProbe, setSuggestionProbe] = React.useState<string | null>(null);
  const [suggestions, setSuggestions] = React.useState<Record<string, UserSearchResult>>({});
  const inputValue = draftValue ?? uncontrolledInputValue;

  const searchText = inputValue.trim() || (suggestionProbe ? suggestionSearchText(suggestionProbe) : '');
  const searchQuery = useUserSearch(searchText);
  const searchResults = searchQuery.data ?? [];
  const shouldShowResults = isFocused && inputValue.trim().length >= 2 && !disabled;

  React.useEffect(() => {
    if (autoFocus && !disabled) {
      inputRef.current?.focus();
    }
  }, [autoFocus, disabled]);

  React.useEffect(() => {
    onInvalidTokensChange?.(invalidTokens);
  }, [invalidTokens, onInvalidTokensChange]);

  React.useEffect(() => {
    setKnownEmails((current) => {
      const next = new Set<string>();
      value.forEach((email) => {
        const normalized = normalizeEmail(email);
        if (current.has(normalized)) {
          next.add(normalized);
        }
      });
      return next;
    });
  }, [value]);

  React.useEffect(() => {
    if (!suggestionProbe || inputValue.trim() || searchQuery.isLoading || searchQuery.isFetching) {
      return;
    }

    if (searchQuery.debouncedQuery !== suggestionSearchText(suggestionProbe)) {
      return;
    }

    const normalizedProbe = normalizeEmail(suggestionProbe);
    const exactMatch = searchResults.find(
      (result) => normalizeEmail(result.email) === normalizedProbe
    );

    if (exactMatch) {
      setKnownEmails((current) => new Set(current).add(normalizedProbe));
      setSuggestionProbe(null);
      return;
    }

    const suggestion = findCloseSuggestion(normalizedProbe, searchResults);
    if (suggestion) {
      setSuggestions((current) => {
        if (current[normalizedProbe]?.email === suggestion.email) {
          return current;
        }

        return {
          ...current,
          [normalizedProbe]: suggestion,
        };
      });
    }
    setSuggestionProbe(null);
  }, [
    inputValue,
    searchQuery.isFetching,
    searchQuery.isLoading,
    searchResults,
    suggestionProbe,
  ]);

  const updateInputValue = (nextValue: string) => {
    if (draftValue === undefined) {
      setUncontrolledInputValue(nextValue);
    }
    onDraftChange?.(nextValue);
    if (nextValue.trim()) {
      setSuggestionProbe(null);
    }
  };

  const addEmails = (tokens: string[], exactResults: UserSearchResult[] = searchResults) => {
    const nextEmails = maxEmails === 1 ? [] : [...value];
    const nextKnownEmails = new Set(knownEmails);
    const nextInvalidTokens: string[] = [];

    tokens.forEach((token) => {
      const normalizedToken = normalizeEmail(token);
      if (!isValidTebraEmail(normalizedToken)) {
        nextInvalidTokens.push(token);
        return;
      }

      const exactMatch = exactResults.find(
        (result) => normalizeEmail(result.email) === normalizedToken
      );
      const emailToAdd = exactMatch ? normalizeEmail(exactMatch.email) : normalizedToken;

      if (!nextEmails.some((email) => normalizeEmail(email) === emailToAdd)) {
        nextEmails.push(emailToAdd);
      }

      if (exactMatch) {
        nextKnownEmails.add(emailToAdd);
      } else {
        const closeSuggestion = findCloseSuggestion(emailToAdd, exactResults);
        if (closeSuggestion) {
          setSuggestions((current) => ({
            ...current,
            [emailToAdd]: closeSuggestion,
          }));
        } else {
          setSuggestionProbe(emailToAdd);
        }
      }
    });

    const constrainedNextEmails =
      maxEmails === undefined || nextEmails.length <= maxEmails
        ? nextEmails
        : nextEmails.slice(nextEmails.length - maxEmails);

    onChange(constrainedNextEmails);
    setKnownEmails(nextKnownEmails);
    setInvalidTokens(nextInvalidTokens);
    updateInputValue('');
  };

  const removeEmail = (emailToRemove: string) => {
    const normalizedEmailToRemove = normalizeEmail(emailToRemove);
    onChange(value.filter((email) => normalizeEmail(email) !== normalizedEmailToRemove));
    setSuggestions((current) => {
      const next = { ...current };
      delete next[normalizedEmailToRemove];
      return next;
    });
  };

  const replaceWithSuggestion = (emailToReplace: string, suggestion: UserSearchResult) => {
    const normalizedEmailToReplace = normalizeEmail(emailToReplace);
    const suggestionEmail = normalizeEmail(suggestion.email);

    onChange(
      value.map((email) =>
        normalizeEmail(email) === normalizedEmailToReplace ? suggestionEmail : email
      )
    );
    setKnownEmails((current) => new Set(current).add(suggestionEmail));
    setSuggestions((current) => {
      const next = { ...current };
      delete next[normalizedEmailToReplace];
      return next;
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    if (TOKEN_SPLIT_REGEX.test(nextValue)) {
      addEmails(splitEmailTokens(nextValue));
      return;
    }

    updateInputValue(nextValue);
    setInvalidTokens([]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (inputValue.trim()) {
        addEmails(splitEmailTokens(inputValue));
      }
      return;
    }

    if ([',', ';', ' ', 'Tab'].includes(event.key) && inputValue.trim()) {
      event.preventDefault();
      addEmails(splitEmailTokens(inputValue));
    }

    if (event.key === 'Backspace' && !inputValue && value.length > 0) {
      removeEmail(value[value.length - 1]);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData('text');
    if (!TOKEN_SPLIT_REGEX.test(pastedText)) {
      return;
    }

    event.preventDefault();
    addEmails(splitEmailTokens(pastedText));
  };

  const handleSelectResult = (result: UserSearchResult) => {
    addEmails([result.email], [result]);
    inputRef.current?.focus();
  };

  return (
    <Popover open={shouldShowResults} onOpenChange={setIsFocused}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            'flex min-h-[82px] w-full flex-wrap items-start gap-2 rounded-md border border-gray-300 bg-background px-3 py-2 text-sm',
            'focus-within:border-gray-300 focus-within:outline-none focus-within:ring-0 focus-within:ring-offset-0',
            disabled && 'cursor-not-allowed opacity-50'
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {value.map((email) => {
            const normalizedEmail = normalizeEmail(email);
            const isNew = !knownEmails.has(normalizedEmail);

            return (
              <Badge
                key={normalizedEmail}
                variant="secondary"
                className="max-w-full gap-1 rounded-md px-2 py-1 text-sm font-normal"
              >
                <span className="truncate">{normalizedEmail}</span>
                {isNew && (
                  <span className="rounded-sm bg-muted px-1 text-[10px] uppercase text-muted-foreground">
                    New
                  </span>
                )}
                <button
                  type="button"
                  className="ml-1 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  aria-label={`Remove ${normalizedEmail}`}
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    removeEmail(normalizedEmail);
                  }}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
          <input
            ref={inputRef}
            id={id}
            type="text"
            aria-label={ariaLabel}
            value={inputValue}
            disabled={disabled}
            placeholder={value.length === 0 ? placeholder : ''}
            className="min-w-[180px] flex-1 border-0 bg-transparent p-0 text-sm outline-none ring-0 placeholder:text-muted-foreground focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-not-allowed"
            onBlur={() => {
              if (commitOnBlur && inputValue.trim()) {
                addEmails(splitEmailTokens(inputValue));
              }
              setIsFocused(false);
            }}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        portalled={false}
        role="presentation"
        className="z-[70] w-[var(--radix-popover-trigger-width)] p-0 bg-background"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Command shouldFilter={false} className="bg-background">
          <CommandList className="bg-background">
            <CommandEmpty>
              {searchQuery.isLoading || searchQuery.isFetching
                ? 'Searching...'
                : 'No matching people.'}
            </CommandEmpty>
            <CommandGroup className="bg-background">
              {searchResults.map((result) => (
                <CommandItem
                  key={result.id}
                  value={result.email}
                  className="flex items-start gap-2 bg-background hover:bg-accent"
                  onPointerDown={(event) => {
                    event.preventDefault();
                    handleSelectResult(result);
                  }}
                  onSelect={() => handleSelectResult(result)}
                >
                  <Check className="mt-0.5 h-4 w-4 opacity-0" />
                  <span className="min-w-0">
                    <span className="block truncate">{result.name || result.email}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {result.email}
                      {result.title ? ` · ${result.title}` : ''}
                    </span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
      {Object.entries(suggestions).map(([email, suggestion]) => (
        <div key={email} className="mt-2 text-sm text-muted-foreground">
          <span>{`Did you mean ${formatUserResult(suggestion)}?`}</span>
          <Button
            type="button"
            variant="link"
            className="h-auto px-2 py-0"
            onClick={() => replaceWithSuggestion(email, suggestion)}
          >
            Replace
          </Button>
        </div>
      ))}
    </Popover>
  );
}
